import { Botao, Selo, SeloDeEmpresa, quantidadeComUnidade } from "@plantus/ui";
import type { Coluna } from "@plantus/ui";
import type { Lote } from "@plantus/api-client";

import { ROTULO_DA_SITUACAO, TOM_DA_SITUACAO } from "./situacaoDoLote";

/**
 * As colunas da lista de lotes.
 *
 * <p>Saíram de dentro de `PaginaLotes.tsx` no recorte do ADR-0013 §2 — o
 * arquivo media 677 linhas contra um teto de 300. Nenhuma definição mudou: o
 * que este arquivo é, é o mesmo `colunas()` de antes, no endereço dele.
 *
 * <p>Fora do componente porque a definição é estável e a lista não: recriar o
 * arranjo a cada tecla digitada na busca é trabalho por nada.
 *
 * <p>`principal` no código do lote — é o que identifica a linha no chão de
 * fábrica, e vira o título do cartão no celular.
 *
 * <p>A coluna de <b>empresa</b> só existe no consolidado, e é a segunda: ela
 * vem logo depois do que identifica a linha, antes de qualquer atributo do
 * material. Quando há uma empresa só, ela seria a mesma palavra repetida em
 * todas as linhas — ruído que ensina o olho a pular a coluna, justamente a que
 * precisa ser lida no dia em que houver duas.
 */
export function colunasDosLotes(
  navegar: (rota: string) => void,
  consolidado: boolean,
): Coluna<Lote>[] {
  const empresa: Coluna<Lote>[] = consolidado
    ? [
        {
          chave: "empresa",
          rotulo: "Empresa",
          // O nome vem do SERVIDOR, junto do lote — não de uma junção com a
          // lista de empresas carregada aqui. Uma junção que não encontrasse a
          // empresa deixaria o selo vazio, e linha sem CNPJ numa tabela de
          // material é o defeito que este selo existe para impedir
          // (ADR-0005 §10).
          conteudo: (l) => <SeloDeEmpresa empresa={l.empresa} />,
        },
      ]
    : [];

  return [
    {
      chave: "codigo",
      rotulo: "Lote",
      principal: true,
      mono: true,
      ordenavel: true,
      conteudo: (l) => l.codigo,
    },
    ...empresa,
    {
      chave: "materiaPrima",
      rotulo: "Matéria-prima",
      conteudo: (l) => (
        <span>
          {l.materiaPrima}
          <small className="lotes__sub">{l.codigoMateriaPrima}</small>
        </span>
      ),
    },
    {
      chave: "fornecedor",
      rotulo: "Fornecedor",
      conteudo: (l) => (
        <span>
          {l.fornecedor}
          {l.loteDoFornecedor !== null && (
            <small className="lotes__sub">lote {l.loteDoFornecedor}</small>
          )}
        </span>
      ),
    },
    {
      chave: "quantidade",
      rotulo: "Quantidade",
      numerica: true,
      // 🔑 Ordenar aqui é do SERVIDOR, e é o caso que prova a regra: o valor
      // chega como texto (`"120.000000"`), e ordenar texto no navegador poria
      // 120 antes de 25 numa coluna que decide o que vai para a formulação.
      ordenavel: true,
      // A quantidade chega como TEXTO do servidor (`120.000000`) e ia crua para
      // a coluna. Em português o ponto é separador de milhar: `120.000000 kg`
      // se lê como cento e vinte MIL quilos, numa tela que decide o que vai
      // para a formulação. Quem sabe ler o número é `quantidadeComUnidade` —
      // a mesma função que a etiqueta usa, porque tela e papel discordarem
      // sobre uma quantidade é pior que os dois errarem juntos.
      conteudo: (l) => quantidadeComUnidade(l.quantidade, l.unidade),
    },
    {
      chave: "validade",
      rotulo: "Validade",
      // "O que vence primeiro" é ORDENAÇÃO, não um segundo filtro de período.
      // O período que a faixa recorta é o de RECEBIMENTO.
      ordenavel: true,
      conteudo: (l) => (
        <span className={l.vencido ? "lotes__vencido" : undefined}>
          {formatar(l.validade)}
          {l.vencido && " · vencido"}
        </span>
      ),
    },
    {
      chave: "situacao",
      rotulo: "Situação",
      ordenavel: true,
      // Rótulo E cor vêm do dado, os dois pelo mesmo mapa. O tom já esteve
      // fixo em "vencendo" aqui: um lote APROVADO sairia com a cor de
      // vencendo, e a cor do status é o que decide o que se faz com o
      // material. Ver `situacaoDoLote.ts`.
      conteudo: (l) => (
        <Selo situacao={TOM_DA_SITUACAO[l.situacao]}>{ROTULO_DA_SITUACAO[l.situacao]}</Selo>
      ),
    },
    {
      chave: "acao",
      rotulo: "Ações",
      rotuloOculto: true,
      // A empresa viaja na rota. A tela de etiqueta precisa de UM CNPJ para
      // carregar o lote e para imprimir, e no consolidado não há empresa
      // escolhida no cabeçalho — quem sabe de qual empresa é aquele lote é a
      // linha em que se clicou.
      conteudo: (l) => (
        <Botao
          aparencia="texto"
          onClick={() =>
            navegar(`/producao/materias-primas/${l.id}/etiqueta?empresa=${l.empresaId}`)
          }
        >
          Etiqueta
        </Botao>
      ),
    },
  ];
}

/** Data do calendário, formatada sem passar por fuso. Ver Etiqueta.tsx. */
function formatar(iso: string): string {
  const [ano, mes, dia] = iso.split("-");
  return `${dia}/${mes}/${ano}`;
}
